import { useState, useMemo } from 'react';
import { BrainOrb, Button, Card, ChipStrip, GlassPill } from '../components/primitives';
import { useAuth } from '../contexts/AuthContext';
import { useBrainMemories, type BrainMemoryRow } from '../lib/queries/useBrainMemories';
import { supabase, isPlaceholderSupabase } from '../lib/supabase';
import './page-shell.css';
import './Memories.css';

const SCOPES: BrainMemoryRow['scope'][] = ['coach', 'team', 'player', 'session', 'drill'];

const SCOPE_LABEL: Record<BrainMemoryRow['scope'], string> = {
  coach: 'About you',
  team: 'About the team',
  player: 'About a player',
  session: 'From a session',
  drill: 'From a drill',
};

export function Memories() {
  const { user } = useAuth();
  const { rows, state, errorMessage, refresh } = useBrainMemories(user?.id);
  const [activeScope, setActiveScope] = useState<BrainMemoryRow['scope'] | null>(null);

  const filtered = useMemo(() => {
    if (!activeScope) return rows;
    return rows.filter((r) => r.scope === activeScope);
  }, [rows, activeScope]);

  const counts = useMemo(() => {
    const map: Record<string, number> = {};
    for (const r of rows) map[r.scope] = (map[r.scope] ?? 0) + 1;
    return map;
  }, [rows]);

  const handleForget = async (id: string) => {
    if (isPlaceholderSupabase()) return;
    const ok = window.confirm('Forget this memory? Brain won\'t reference it again.');
    if (!ok) return;
    // Soft-delete to honour the schema convention.
    const { error } = await supabase
      .from('brain_memories')
      .update({ deleted_at: new Date().toISOString() })
      .eq('id', id);
    if (!error) refresh();
  };

  return (
    <main className="main page memories-page">
      <header className="page-head">
        <BrainOrb size="lg" ariaLabel="Brain" />
        <div>
          <span className="page-eyebrow">Layer 1 · Brain memory</span>
          <h1 className="page-title">What Brain remembers</h1>
          <p className="page-sub">
            Everything Brain has learned about you, your team, and your players. Forget any of it any time.
          </p>
        </div>
      </header>

      {state === 'loading' && <p className="page-empty">Loading…</p>}
      {state === 'error' && (
        <Card variant="opaque">
          <p className="page-empty">
            Couldn&apos;t load memories.{errorMessage ? ` (${errorMessage})` : null}
          </p>
        </Card>
      )}
      {state === 'empty' && (
        <Card variant="opaque">
          <p className="page-empty">
            Brain hasn&apos;t learned anything durable yet. As you chat and approve drafts,
            Brain will save preferences, patterns, and traits — they&apos;ll all surface here.
          </p>
        </Card>
      )}

      {state === 'ok' && (
        <>
          <ChipStrip
            chips={[
              { label: `all · ${rows.length}`, value: '__all', active: activeScope === null },
              ...SCOPES.filter((s) => (counts[s] ?? 0) > 0).map((s) => ({
                label: `${SCOPE_LABEL[s]} · ${counts[s]}`,
                value: s,
                active: activeScope === s,
              })),
            ]}
            onSelect={(value) => setActiveScope(value === '__all' ? null : (value as BrainMemoryRow['scope']))}
          />

          <ul className="memories-list">
            {filtered.map((m) => (
              <li key={m.id}>
                <Card variant="opaque">
                  <div className="memory-row">
                    <div className="memory-row__pills">
                      <GlassPill mono>{SCOPE_LABEL[m.scope]}</GlassPill>
                      <GlassPill mono>{m.kind}</GlassPill>
                      <GlassPill mono>conf {Math.round(m.confidence * 100)}%</GlassPill>
                      {m.tags.slice(0, 3).map((t) => (
                        <GlassPill key={t} mono>{t}</GlassPill>
                      ))}
                    </div>
                    <p className="memory-row__fact">{m.fact}</p>
                    <div className="memory-row__foot">
                      <span className="memory-row__date">
                        {new Date(m.created_at).toLocaleDateString()}
                      </span>
                      <Button variant="glass" size="touchline" onClick={() => void handleForget(m.id)}>
                        Forget
                      </Button>
                    </div>
                  </div>
                </Card>
              </li>
            ))}
          </ul>
        </>
      )}
    </main>
  );
}
